import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import {
  getActiveStoreCatalog,
  ownsStoreStyleItem,
  getStoreBuyPayload,
  STORE_WINNER_OFFERING_COUNT,
  STORE_AUGMENT_OFFERING_COUNT
} from '../utils/augments';
import './Store.css';

const COIN_PACK_ID = 'coins15';
const COIN_PAYMENT_SUCCESS =
  'Payment received — coins will appear in your balance shortly.';
const COIN_PAYMENT_SUCCESS_DISMISS_MS = 10000;

function formatRefreshCountdown(msRemaining) {
  const ms = Math.max(0, msRemaining);
  const totalSec = Math.floor(ms / 1000);
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

function StoreStyleCard({ item, inventory, coins, loading, itemKey, setConfirmItem }) {
  const owned = ownsStoreStyleItem(inventory, item);
  const canAfford = coins >= item.cost;
  const isBuying = loading === itemKey(item);
  const styleSvgs = item.styleSvgs?.length ? item.styleSvgs : [item.styleSvg].filter(Boolean);
  const isPair = styleSvgs.length > 1;

  return (
    <div
      className={`store-card store-card--${item.color} ${owned ? 'store-card--owned' : ''}`}
    >
      <div className={`store-card-art${isPair ? ' store-card-art--pair' : ''}`}>
        {styleSvgs.map((src) => (
          <img key={src} src={src} alt="" className="store-card-style-img" />
        ))}
      </div>
      <div className="store-card-titles">
        <p className="store-card-name">{item.label}</p>
        <p className="store-card-style-name">{item.styleName}</p>
      </div>
      <div className="store-card-footer">
        <span className="store-card-price">
          <img src="/coin.svg" alt="" className="store-coins-icon" /> {item.cost}
        </span>
        {owned ? (
          <span className="store-card-owned">Owned</span>
        ) : (
          <button
            type="button"
            className="store-btn-buy"
            data-testid="store-buy-btn"
            disabled={!canAfford || isBuying}
            onClick={() => setConfirmItem(item)}
          >
            {isBuying ? 'Buying…' : canAfford ? 'Buy' : 'Need more coins'}
          </button>
        )}
      </div>
    </div>
  );
}

const Store = () => {
  const { user, updateToken, refreshUser } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(null);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [confirmItem, setConfirmItem] = useState(null);
  const [now, setNow] = useState(() => Date.now());
  const coinReturnHandled = useRef(false);

  const coins = user?.coinBalance ?? 0;
  const inventory = user?.inventory ?? {};

  const { items: offerItems, windowEnd } = useMemo(() => getActiveStoreCatalog(now), [now]);

  // Unowned first so players see what they can still buy; winner icons and play styles are separate sections
  const { winnerItems, augmentItems } = useMemo(() => {
    const sortUnownedFirst = (items) =>
      [...items].sort((a, b) => {
        const aOwned = ownsStoreStyleItem(inventory, a) ? 1 : 0;
        const bOwned = ownsStoreStyleItem(inventory, b) ? 1 : 0;
        return aOwned - bOwned;
      });
    const winners = [];
    const augments = [];
    for (const item of offerItems) {
      if (item.kind === 'winner') winners.push(item);
      else augments.push(item);
    }
    return {
      winnerItems: sortUnownedFirst(winners),
      augmentItems: sortUnownedFirst(augments)
    };
  }, [offerItems, inventory]);

  useEffect(() => {
    if (!confirmItem) return;
    const onKey = (e) => {
      if (e.key === 'Escape') setConfirmItem(null);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [confirmItem]);

  useEffect(() => {
    const tick = () => {
      const t = Date.now();
      setNow(t);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [windowEnd]);

  useEffect(() => {
    if (coinReturnHandled.current) return;
    const coinsParam = searchParams.get('coins');
    if (coinsParam !== 'success' && coinsParam !== 'cancel') return;
    coinReturnHandled.current = true;
    const sessionId = searchParams.get('session_id');
    setSearchParams({}, { replace: true });
    if (coinsParam === 'success') {
      setSuccess(COIN_PAYMENT_SUCCESS);
      (async () => {
        try {
          if (sessionId) {
            const { data } = await api.post('/store/confirm-checkout', { sessionId });
            if (data?.token) {
              updateToken(data.token, { coinBalance: data.coinBalance });
              return;
            }
          }
          await refreshUser();
        } catch {
          refreshUser().catch(() => {});
        }
      })();
    } else {
      setError('Checkout cancelled. No charge was made.');
    }
  }, [searchParams, setSearchParams, refreshUser, updateToken]);

  useEffect(() => {
    if (success !== COIN_PAYMENT_SUCCESS) return undefined;
    const t = setTimeout(() => setSuccess(''), COIN_PAYMENT_SUCCESS_DISMISS_MS);
    return () => clearTimeout(t);
  }, [success]);

  const itemKey = (item) => `${item.id}#${item.styleId}`;
  const msRemaining = windowEnd - now;

  const handleBuyCoins = async () => {
    if (checkoutLoading) return;
    setError('');
    setSuccess('');
    setCheckoutLoading(true);
    try {
      const { data } = await api.post('/store/checkout', { packId: COIN_PACK_ID });
      if (!data?.url) {
        setError('Could not start checkout');
        setCheckoutLoading(false);
        return;
      }
      window.location.assign(data.url);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not start checkout');
      setCheckoutLoading(false);
    }
  };

  const handleConfirmPurchase = async () => {
    const item = confirmItem;
    if (!item || loading) return;
    setConfirmItem(null);
    setError('');
    setSuccess('');
    setLoading(itemKey(item));
    try {
      const { data } = await api.post('/store/buy', getStoreBuyPayload(item));
      if (data.token) {
        updateToken(data.token, { coinBalance: data.coinBalance, inventory: data.inventory });
      }
      setSuccess(`Purchased ${item.styleName} for ${item.label}!`);
      setLoading(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Purchase failed');
      setLoading(null);
    }
  };

  if (!user) {
    return null;
  }

  return (
    <div className="store-page">
      <header className="store-header">
        <div className="store-header-top">
          <div className="store-header-copy">
            <h1 className="store-title">Store</h1>
            <p className="store-subtitle">
              Up to {STORE_WINNER_OFFERING_COUNT} winner icons and {STORE_AUGMENT_OFFERING_COUNT}{' '}
              play styles — rotates every 8 hours
            </p>
          </div>
          <button
            type="button"
            className="store-btn-buy-coins"
            data-testid="store-buy-coins-btn"
            disabled={checkoutLoading}
            onClick={handleBuyCoins}
          >
            {checkoutLoading ? (
              'Redirecting…'
            ) : (
              <>
                Buy 15 <img src="/coin.svg" alt="" className="store-coins-icon" />
              </>
            )}
          </button>
        </div>
        <div className="store-header-meta">
          <div className="store-balance">
            <span className="store-balance-label">Your balance</span>
            <span className="store-balance-value">
              <img src="/coin.svg" alt="" className="store-coins-icon" />
              {coins} coins
            </span>
          </div>
          <p className="store-refresh" data-testid="store-refresh-countdown">
            Refreshes in {formatRefreshCountdown(msRemaining)}
          </p>
        </div>
      </header>

      {error && <div className="store-message store-error">{error}</div>}
      {success && <div className="store-message store-success">{success}</div>}

      {offerItems.length === 0 ? (
        <p className="store-empty">No styles are for sale right now.</p>
      ) : (
        <>
          {winnerItems.length > 0 && (
            <section className="store-section" aria-labelledby="store-section-winners">
              <h2 id="store-section-winners" className="store-section-title">
                Winner icons
              </h2>
              <div className="store-grid" data-testid="store-winner-icons-grid">
                {winnerItems.map((item) => (
                  <StoreStyleCard
                    key={itemKey(item)}
                    item={item}
                    inventory={inventory}
                    coins={coins}
                    loading={loading}
                    itemKey={itemKey}
                    setConfirmItem={setConfirmItem}
                  />
                ))}
              </div>
            </section>
          )}
          {augmentItems.length > 0 && (
            <section className="store-section" aria-labelledby="store-section-augments">
              <h2 id="store-section-augments" className="store-section-title">
                Play Styles
              </h2>
              <div className="store-grid" data-testid="store-offerings-grid">
                {augmentItems.map((item) => (
                  <StoreStyleCard
                    key={itemKey(item)}
                    item={item}
                    inventory={inventory}
                    coins={coins}
                    loading={loading}
                    itemKey={itemKey}
                    setConfirmItem={setConfirmItem}
                  />
                ))}
              </div>
            </section>
          )}
        </>
      )}

      {confirmItem && (
        <div
          className="store-modal-backdrop"
          role="presentation"
          onClick={() => setConfirmItem(null)}
        >
          <div
            className="store-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="store-modal-title"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 id="store-modal-title" className="store-modal-title">
              Confirm purchase
            </h2>
            <p className="store-modal-body">
              Buy <strong>{confirmItem.styleName}</strong> for <strong>{confirmItem.label}</strong> for{' '}
              <strong>{confirmItem.cost} coins</strong>? Your balance after:{' '}
              <strong>{coins - confirmItem.cost}</strong> coins.
            </p>
            <div className="store-modal-actions">
              <button type="button" className="store-modal-btn store-modal-btn--no" onClick={() => setConfirmItem(null)}>
                No
              </button>
              <button
                type="button"
                className="store-modal-btn store-modal-btn--yes"
                data-testid="store-confirm-buy"
                onClick={handleConfirmPurchase}
              >
                Yes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Store;
